import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

interface PresentationProps {
  isMobile?: boolean
}

const fonts = [
  { family: 'Inter, sans-serif', name: 'inter' },
  { family: 'Roboto, sans-serif', name: 'roboto' },
  { family: 'Poppins, sans-serif', name: 'poppins' },
  { family: 'Montserrat, sans-serif', name: 'montserrat' },
  { family: 'Oswald, sans-serif', name: 'oswald' },
  { family: 'Raleway, sans-serif', name: 'raleway' },
  { family: 'Work Sans, sans-serif', name: 'worksans' },
  { family: 'Manrope, sans-serif', name: 'manrope' },
  { family: 'Outfit, sans-serif', name: 'outfit' },
  { family: 'Space Grotesk, sans-serif', name: 'space' }
]

export function Presentation({ isMobile = false }: PresentationProps) {
  // Su mobile: colore fisso per evitare problemi di performance
  const [gradientColor, setGradientColor] = useState(isMobile ? '#7ec876' : '#7ec876')
  
  // Font rotation for "connect different worlds"
  const [fontIndex, setFontIndex] = useState(0)
  
  useEffect(() => {
    if (isMobile) return
    const interval = setInterval(() => {
      setFontIndex((prev) => (prev + 1) % fonts.length)
    }, 600)
    return () => clearInterval(interval)
  }, [isMobile])

  useEffect(() => {
    if (isMobile) {
      // Su mobile: nessun cambio colore dinamico
      return
    }

    const colors = [
      '#7ec876', // verde originale
      '#4da6ff', // blu
      '#ff6b6b', // rosso
      '#ffd93d', // giallo
      '#a78bfa', // viola
      '#f472b6', // rosa
      '#34d399', // verde menta
      '#f59e0b'  // arancione
    ]

    const interval = setInterval(() => {
      const randomColor = colors[Math.floor(Math.random() * colors.length)]
      setGradientColor(randomColor)
    }, 3333)

    return () => clearInterval(interval)
  }, [isMobile])

  return (
    <section className="py-12 md:py-20 z-20 relative">
      {/* Gradiente: grigio #CFCFCF → colore dinamico → verde #7ec876 */}
      <div 
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background: `linear-gradient(to bottom, #CFCFCF 0%, ${gradientColor} 85%, #7ec876 100%)`
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        {/* Video - immediately visible, no title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: isMobile ? 0.3 : 0.8, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12 md:mb-20"
        >
          {/* Empty space (1 column) */}
          <div className="lg:col-span-1"></div>

          {/* Video (2 columns) */}
          <div className="lg:col-span-2 flex justify-center">
            <div className="aspect-video md:aspect-[16/9] w-full max-w-md lg:max-w-none bg-gray-900 rounded-lg overflow-hidden shadow-2xl">
              <video
                src="/VIDEO/reel24.mov"
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
            </div>
          </div>
        </motion.div>

        {/* Text (3 columns) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: isMobile ? 0.3 : 0.8, delay: 0.2, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12 md:mb-20"
        >
          <div className="lg:col-span-3 relative z-30 overflow-visible">
            <div className="text-[36px] md:text-[51px] lg:text-[73px] leading-[1.05] text-[#444444] tracking-[-0.035em] font-light">
              <span>Digital Designer passionate about communication and pixels, I cultivate a multidisciplinary approach to learn how to </span>
              <span className="whitespace-nowrap" style={isMobile ? {} : { fontFamily: fonts[fontIndex].family }}>connect different worlds</span>.
            </div>
          </div>
        </motion.div>

        {/* Photo + Capabilities Table (3-column layout) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: isMobile ? 0.3 : 0.8, delay: 0.4, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12 md:mb-20"
        >
          {/* Photo (1 column) */}
          <div className="lg:col-span-1 relative">
            {/* ft.png image */}
            <motion.div
              className="hidden lg:block w-full h-96 opacity-50 relative z-20"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              whileInView={{ opacity: 0.5, scale: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: isMobile ? 0.3 : 0.8, delay: 0.2, ease: "easeOut" }}
            >
              <img
                src="/IMG/COLOR.png"
                alt="color"
                className="w-full h-full object-contain"
              />
            </motion.div>
            {/* Mobile version */}
            <motion.div
              className="lg:hidden w-full h-64 mx-auto relative z-20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: isMobile ? 0.3 : 0.8, delay: 0.3, ease: "easeOut" }}
            >
              <img
                src="/IMG/COLOR.png"
                alt="color"
                className="w-full h-full object-contain opacity-50"
              />
            </motion.div>
          </div>

          {/* Capabilities Table (2 columns) */}
          <div className="lg:col-span-2">
            <motion.h3 
              className="text-lg md:text-2xl font-bold text-[#444444] mb-6 md:mb-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: isMobile ? 0.3 : 0.8, delay: 0.5, ease: "easeOut" }}
            >
              Capabilities
            </motion.h3>
            <motion.div
              className="space-y-3 md:space-y-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: isMobile ? 0.3 : 0.8, delay: 0.6, ease: "easeOut" }}
            >
              {[
                "Digital Design & Branding",
                "UX/UI Design & Prototyping", 
                "3D Visualization & Animation",
                "Interactive Web Development",
                "Motion Graphics & Video",
                "Art Direction & Strategy",
                "Photography & Visual Content",
                "AI Integration & Innovation"
              ].map((capability, index) => (
                <motion.div 
                  key={index} 
                  className="border-b border-[#444444]/30 pb-2"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: isMobile ? 0.2 : 0.6, delay: 0.7 + (index * 0.1), ease: "easeOut" }}
                >
                  <span className="text-[#444444] text-sm font-medium">{capability}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Clients Strip - reduced margin (55% less) */}
        <motion.div
          className="relative mb-4 md:mb-5 z-30 py-2 rounded-lg w-screen left-1/2 -translate-x-1/2"
          style={{ backgroundColor: '#CFCFCF' }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.3 : 1 }}
        >
          <div className="flex items-center justify-center overflow-hidden">
            <motion.div
              className="flex items-center gap-4 md:gap-8 text-sm md:text-lg font-semibold whitespace-nowrap"
              animate={{
                x: ["0px", "-50%"],
              }}
              transition={{
                duration: isMobile ? 36.36 : 72.72,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              {/* clients */}
              {[
                "LAVAZZA", "REALE MUTUA", "LAURETANA", "IVECO", "LAVAZZA GROUP",
                "FPT", "DISTINTO", "C2C", "EXPO", "JUVENTUS", "OLSA", "RAI",
                "PARCO DEL BEIGUA", "OMG", "STUDIO SOSPESO", "ARTESANA", "CASA BOMBO",
                "LEMON LABEL", "KEEPINGFAKA", "R&M", "ERACLEA", "MAGNETI MARELLI", "ÖOSTERIA"
              ].map((client, index) => (
                <div key={index} className="flex items-center justify-center gap-2 md:gap-4 px-4 md:px-8 py-2 md:py-3">
                  <div className="w-px h-3 md:h-5 bg-[#444444]/50 mx-2 md:mx-4" />
                  <span className="text-[#444444] text-center">{client}</span>
                  <div className="w-px h-4 md:h-6 bg-[#444444]/50 mx-2 md:mx-4" />
                </div>
              ))}
              
              {/* Duplicate for seamless infinite loop */}
              {[
                "LAVAZZA", "REALE MUTUA", "LAURETANA", "IVECO", "LAVAZZA GROUP",
                "FPT", "DISTINTO", "C2C", "EXPO", "JUVENTUS", "OLSA", "RAI",
                "PARCO DEL BEIGUA", "OMG", "STUDIO SOSPESO", "ARTESANA", "CASA BOMBO",
                "LEMON LABEL", "KEEPINGFAKA", "R&M", "ERACLEA", "MAGNETI MARELLI", "ÖOSTERIA"
              ].map((client, index) => (
                <div key={`duplicate-${index}`} className="flex items-center justify-center gap-2 md:gap-4 px-4 md:px-8 py-2 md:py-3">
                  <div className="w-px h-3 md:h-5 bg-[#444444]/50 mx-2 md:mx-4" />
                  <span className="text-[#444444] text-center">{client}</span>
                  <div className="w-px h-3 md:h-5 bg-[#444444]/50 mx-2 md:mx-4" />
                </div>
              ))}
              
              {/* Third set for extra safety in infinite loop */}
              {[
                "LAVAZZA", "REALE MUTUA", "LAURETANA", "IVECO", "LAVAZZA GROUP",
                "FPT", "DISTINTO", "C2C", "EXPO", "JUVENTUS", "OLSA", "RAI",
                "PARCO DEL BEIGUA", "OMG", "STUDIO SOSPESO", "ARTESANA", "CASA BOMBO",
                "LEMON LABEL", "KEEPINGFAKA", "R&M", "ERACLEA", "MAGNETI MARELLI", "ÖOSTERIA"
              ].map((client, index) => (
                <div key={`third-${index}`} className="flex items-center justify-center gap-2 md:gap-4 px-4 md:px-8 py-2 md:py-3">
                  <div className="w-px h-3 md:h-5 bg-[#444444]/50 mx-2 md:mx-4" />
                  <span className="text-[#444444] text-center">{client}</span>
                  <div className="w-px h-3 md:h-5 bg-[#444444]/50 mx-2 md:mx-4" />
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Text - between clients strip and cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: isMobile ? 0.3 : 0.8, ease: "easeOut" }}
          className="container mx-auto px-4 relative z-10 py-8 md:py-12"
        >
          <p className="text-[36px] md:text-[51px] lg:text-[73px] leading-[1.05] text-[#444444] tracking-[-0.035em] font-light text-left">
            I am committed to exploring every platform with curiosity, seeking cross-cutting and concrete solutions.
          </p>
        </motion.div>

      </div>
    </section>
  )
}
